<template>
  <div class="model-comparison">
    <!-- Controls Bar -->
    <div class="controls-bar">
      <!-- Location Picker -->
      <div class="control-group location-group">
        <label class="control-label">Location</label>
        <div class="location-input-wrapper">
          <input 
            type="text" 
            class="form-control form-control-sm location-input"
            v-model="locationText"
            @keyup.enter="geocodeLocation"
            placeholder="Enter location..."
          />
          <button class="btn btn-sm btn-outline-primary" @click="geocodeLocation" :disabled="isGeocoding">
            <i class="bi" :class="isGeocoding ? 'bi-arrow-repeat spin-icon' : 'bi-search'"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="useCurrentLocation" :disabled="isGeocoding">
            <i class="bi bi-geo"></i>
          </button>
        </div>
        <div v-if="location" class="location-display">
          <i class="bi bi-geo-alt-fill"></i>
          {{ location.name || `${location.lat?.toFixed(4)}, ${location.lon?.toFixed(4)}` }}
        </div>
      </div>

      <!-- Date Range -->
      <div class="control-group">
        <label class="control-label">Date Range</label>
        <div class="date-inputs">
          <input type="date" class="form-control form-control-sm" v-model="startDate" />
          <span class="date-separator">to</span>
          <input type="date" class="form-control form-control-sm" v-model="endDate" />
        </div>
        <div class="quick-dates">
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('today')">Today</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('3days')">3 Days</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('week')">Week</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('2weeks')">2 Weeks</a>
        </div>
      </div>

      <!-- Time Resolution -->
      <div class="control-group">
        <label class="control-label">Resolution</label>
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="timeResolution === 'hours' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'hours'"
          >Hourly</button>
          <button 
            class="btn" 
            :class="timeResolution === 'days' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'days'"
          >Daily</button>
        </div>
      </div>

      <!-- Unit Group -->
      <div class="control-group">
        <label class="control-label">Units</label>
        <select v-model="unitGroup" class="form-control form-control-sm" style="width: auto;">
          <option value="metric">Metric</option>
          <option value="us">US</option>
          <option value="uk">UK</option>
          <option value="base">Base (SI)</option>
        </select>
      </div>

      <!-- API Options -->
      <div class="control-group options-group">
        <div class="option-checkboxes">
          <label class="option-checkbox" title="Use forecast data instead of observations">
            <input type="checkbox" v-model="optionUsefcst" />
            <span>usefcst</span>
          </label>
          <label class="option-checkbox" title="Disable interpolation between model grid points">
            <input type="checkbox" v-model="optionNointerpolate" />
            <span>nointerpolate</span>
          </label>
          <label class="option-checkbox" title="Include model attribution in response">
            <input type="checkbox" v-model="optionAttribution" />
            <span>attribution</span>
          </label>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="control-group fetch-group">
        <label class="control-label">&nbsp;</label>
        <button 
          class="btn btn-sm fetch-btn"
          :class="needsRefresh ? 'btn-warning' : 'btn-primary'"
          @click="fetchData"
          :disabled="!canFetch || isLoading"
          :title="needsRefresh ? 'Settings have changed - click to refresh' : 'Fetch and compare model data'"
        >
          <i class="bi" :class="isLoading ? 'bi-arrow-repeat spin-icon' : (needsRefresh ? 'bi-arrow-repeat' : 'bi-download')"></i>
          {{ isLoading ? 'Loading...' : (needsRefresh ? 'Refresh' : 'Compare Models') }}
        </button>
      </div>

      <!-- API Key Status -->
      <div class="control-group api-status-group">
        <!--<div class="api-key-status" v-if="hasValidApiKey">
          <i class="bi bi-key-fill text-success"></i>
          <span v-if="hasSession">Session key</span>
          <span v-else>API key set</span>
        </div>
        -->
        <div class="api-key-status text-danger" v-if="!hasValidApiKey">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span>Please log in</span>
        </div>
        <button class="btn btn-sm btn-outline-secondary" @click="showHelpModal = true">
          <i class="bi bi-question-circle"></i>
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="comparison-content">
      <!-- Model Selection Panel (Left) -->
      <div class="model-panel" :class="{ collapsed: modelPanelCollapsed }">
        <div class="panel-header" @click="modelPanelCollapsed = !modelPanelCollapsed">
          <span>Models</span>
          <span class="selected-count">{{ selectedModelCount }}/{{ totalModelCount }}</span>
          <i class="bi" :class="modelPanelCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!modelPanelCollapsed">
          <!-- Mode Toggle Bar -->
          <div class="mode-toggle-bar">
            <button 
              class="mode-btn" 
              :class="{ active: !showModelRuns }" 
              @click="onModelRunsToggle(false)">
              <i class="bi bi-box me-1"></i>Models
            </button>
            <button 
              class="mode-btn" 
              :class="{ active: showModelRuns }" 
              @click="onModelRunsToggle(true)">
              <i class="bi me-1" :class="modelRunsLoading ? 'bi-arrow-repeat spin-icon' : 'bi-clock-history'"></i>Model Runs
            </button>
          </div>
          
          <!-- Selected Model Runs List -->
          <div v-if="showModelRuns && selectedModelRuns.length > 0" class="selected-runs-list">
            <div class="selected-runs-header">
              <span>Selected ({{ selectedModelRuns.length }})</span>
              <a href="#" class="clear-link" @click.prevent="clearSelectedRuns">Clear</a>
            </div>
            <div class="selected-runs-items">
              <div v-for="(run, idx) in selectedModelRuns" :key="idx" class="selected-run-item" :style="{ borderLeftColor: getRunColor(idx) }">
                <span class="run-model-name">{{ run.displayName }}</span>
                <span class="run-time">{{ formatRunTime(run.runTime) }}</span>
                <button class="btn btn-xs" @click="removeSelectedRun(idx)" title="Remove">
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- V2 Models Loading indicator -->
          <div v-if="v2ModelsLoading" class="loading-models">
            <i class="bi bi-arrow-repeat spin-icon"></i> Loading V2 models from API...
          </div>

          <!-- Model Groups -->
          <div class="model-groups">
            <!-- Clear All Link -->
            <div v-if="!showModelRuns" class="models-header-bar">
              <a href="#" class="clear-link" @click.prevent="selectPreset('none')" v-if="selectedModels.length > 0">clear</a>
            </div>
            
            <!-- Model Combinations (Default Models) -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header combinations-header" @click="toggleGroup('combinations')">
                <i class="bi bi-stack me-1"></i>
                <span>Model Combinations</span>
                <i class="bi ms-auto" :class="expandedGroups.combinations ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.combinations">
                <label class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" value="default" />
                  <span class="model-color" style="background-color: #000000;"></span>
                  <span class="model-name">Default V1</span>
                </label>
                <label class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" value="default_v2" />
                  <span class="model-color" style="background-color: #2E7D32;"></span>
                  <span class="model-name">Default V2</span>
                </label>
              </div>
            </div>

            <!-- V2 Models (from API) - Standard Mode -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header v2-header" @click="toggleGroup('v2')">
                <i class="bi bi-lightning-fill me-1 text-success"></i>
                <span>V2 Models</span>
                <span class="group-count">{{ modelGroups.v2.length }}</span>
                <i class="bi ms-auto" :class="expandedGroups.v2 ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.v2">
                <div v-if="!v2ModelsLoaded && !v2ModelsLoading" class="no-models-msg">
                  <i class="bi bi-info-circle"></i> Click "Fetch Data" to load V2 models
                </div>
                <label v-for="model in modelGroups.v2" :key="`${model.id}-${locationKey}`" class="model-checkbox" :class="{ 'out-of-bounds': !isModelInBounds(model) }">
                  <span v-if="!isModelInBounds(model)" class="out-bounds-icon" title="Model not available for this location"><i class="bi bi-slash-circle"></i></span>
                  <input v-else type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span class="model-badge badge-v2">V2</span>
                </label>
              </div>
            </div>
            
            <!-- V2 Models - Model Runs Mode -->
            <div class="model-runs-container" v-if="showModelRuns">
              <div v-if="modelRunsLoading" class="runs-loading">
                <i class="bi bi-arrow-repeat spin-icon me-1"></i> Loading available runs...
              </div>
              <div v-else-if="Object.keys(modelRunsData).length === 0" class="no-runs-msg">
                <i class="bi bi-info-circle me-1"></i> No model runs loaded. Click refresh to load available runs.
              </div>
              <div v-else class="runs-by-model">
                <div v-for="model in modelsWithRuns" :key="model.id" class="model-runs-group">
                  <div class="model-runs-title">
                    <span class="model-color-dot" :style="{ backgroundColor: model.color }"></span>
                    <span class="model-name">{{ model.name }}</span>
                  </div>
                  <div class="runs-by-date-container">
                    <div v-for="dateGroup in getRunsByDate(model.id)" :key="dateGroup.date" class="runs-date-group">
                      <div class="runs-date-label">{{ dateGroup.dateLabel }}</div>
                      <div class="runs-time-buttons">
                        <button 
                          v-for="run in dateGroup.runs" 
                          :key="run.runId"
                          class="run-time-btn"
                          :class="{ selected: isRunSelected(model.id, run.runId) }"
                          @click="toggleRunSelection(model.id, run)"
                          :title="formatRunTime(run.runTime)">
                          {{ formatRunHour(run.runTime) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Legacy Models -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header legacy-header" @click="toggleGroup('legacy')">
                <i class="bi bi-clock-history me-1 text-warning"></i>
                <span>Legacy Models</span>
                <span class="group-count">{{ modelGroups.legacy.length }}</span>
                <i class="bi ms-auto" :class="expandedGroups.legacy ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.legacy">
                <label v-for="model in modelGroups.legacy" :key="model.id" class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span class="model-badge badge-legacy">Legacy</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart & Results Area (Center) -->
      <div class="results-area">
        <!-- Element Selector -->
        <div class="element-selector">
          <label class="control-label">Element:</label>
          <select class="form-select form-select-sm" v-model="selectedElement">
            <option v-for="el in availableElements" :key="el.id" :value="el.id">
              {{ el.name }}
            </option>
          </select>
          
          <label class="control-label ms-3">Baseline:</label>
          <select class="form-select form-select-sm" v-model="baselineModel">
            <option value="">None</option>
            <option v-for="model in selectedModelConfigs" :key="model.id" :value="model.id">
              {{ model.name }}{{ hasV2Version(model.id) ? ' ⚠️ Legacy' : '' }}{{ isV2Model(model.id) ? ' ✓ V2' : '' }}
            </option>
          </select>

          <div class="view-toggle ms-auto">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn" 
                :class="viewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'chart'"
              ><i class="bi bi-graph-up"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'table'"
              ><i class="bi bi-table"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'diff' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'diff'"
                :disabled="!baselineModel"
              ><i class="bi bi-arrows-expand"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'map' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'map'"
                title="Map View"
              ><i class="bi bi-map"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'split' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'split'"
                title="Chart + Map Split View"
              ><i class="bi bi-layout-split"></i></button>
            </div>
          </div>
        </div>

        <!-- Chart View -->
        <div class="chart-container" v-show="viewMode === 'chart' || viewMode === 'diff'">
          <div class="comparison-chart" ref="chartContainer">
            <svg ref="chartSvg"></svg>
            <div class="chart-tooltip" ref="chartTooltip">
              <div class="tooltip-content"></div>
            </div>
          </div>
          
          <!-- Chart Legend -->
          <div class="chart-legend" v-if="hasData">
            <div 
              v-for="model in selectedModelConfigs" 
              :key="model.id" 
              class="legend-item"
              :class="{ 
                dimmed: highlightedModel && highlightedModel !== model.id,
                'is-ensemble': model.isEnsemble 
              }"
              @mouseenter="highlightModel(model.id)"
              @mouseleave="highlightModel(null)"
            >
              <span class="legend-color" :style="{ backgroundColor: model.color }"></span>
              <span class="legend-name">{{ model.name }}</span>
              <span class="legend-spread-indicator" v-if="model.isEnsemble && showEnsembleSpread">
                <i class="bi bi-graph-up"></i>
              </span>
            </div>
          </div>
        </div>

        <!-- Table View -->
        <div class="table-container" v-show="viewMode === 'table'">
          <table class="table table-sm table-striped table-hover">
            <thead>
              <tr>
                <th class="sticky-col">Time</th>
                <th 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :style="{ borderTop: `3px solid ${model.color}` }"
                  :class="{ 'legacy-model': hasV2Version(model.id), 'v2-model': isV2Model(model.id) }"
                >
                  {{ model.name }}
                  <span v-if="hasV2Version(model.id)" class="th-legacy" title="Legacy - V2 version available">⚠️</span>
                  <span v-if="isV2Model(model.id)" class="th-v2" title="V2 Model">✓</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableData" :key="row.datetime">
                <td class="sticky-col">{{ formatDateTime(row.datetime) }}</td>
                <td 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :class="getCellClass(row, model.id)"
                >
                  {{ formatValue(row[model.id]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Map View -->
        <div class="map-view-container" v-if="viewMode === 'map'">
          <div class="map-info-bar">
            <span class="map-info-text">
              <i class="bi bi-geo-alt-fill me-1"></i>
              {{ location?.name || 'Select location' }}
            </span>
            <span class="map-info-text ms-3">
              <i class="bi bi-stack me-1"></i>
              {{ mapElementName }}
            </span>
            <span class="map-info-text ms-3" v-if="mapSelectedModel">
              <i class="bi bi-box me-1"></i>
              Model: {{ mapSelectedModelName }}
            </span>
          </div>
          <div class="embedded-map-wrapper">
            <WeatherMapAi ref="weatherMap" 
              :allowed-models="mapAllowedModels" 
              :allowed-elements="[mapElement]"
              :initial-element="mapElement"
              :initial-model="mapInitialModel"
              :unit-group="unitGroup"
              :skip-default-load="true" />
            <!-- Empty State Overlay for Map -->
            <div class="map-empty-overlay" v-if="!hasData && !isLoading">
              <div class="empty-state-content">
                <i class="bi bi-map icon-3x mb-3"></i>
                <h5>No Data Yet</h5>
                <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Split View (Chart + Map) -->
        <div class="split-view-container" v-if="viewMode === 'split'" ref="splitViewContainer">
          <!-- Empty State Overlay for Split -->
          <div class="split-empty-overlay" v-if="!hasData && !isLoading">
            <div class="empty-state-content">
              <i class="bi bi-layout-split icon-3x mb-3"></i>
              <h5>No Data Yet</h5>
              <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
            </div>
          </div>
          <div class="split-chart-section" :style="{ height: splitChartHeight + 'px', flex: 'none' }">
            <div class="comparison-chart" ref="splitChartContainer">
              <svg ref="splitChartSvg"></svg>
              <div class="chart-tooltip" ref="splitChartTooltip">
                <div class="tooltip-content"></div>
              </div>
            </div>
          </div>
          <div class="split-resize-handle" 
               @mousedown="startSplitResize"
               @touchstart.prevent="startSplitResize">
            <div class="resize-handle-bar"></div>
          </div>
          <div class="split-map-section">
            <div class="map-info-bar">
              <span class="map-info-text">
                <i class="bi bi-stack me-1"></i>
                {{ mapElementName }}
              </span>
              <span class="map-info-text ms-2" v-if="mapSelectedModel">
                <i class="bi bi-box me-1"></i>
                {{ mapSelectedModelName }}
              </span>
            </div>
            <div class="embedded-map-wrapper">
              <WeatherMapAi ref="weatherMapSplit" 
                :allowed-models="mapAllowedModels" 
                :allowed-elements="[mapElement]"
                :initial-element="mapElement"
                :initial-model="mapInitialModel"
                :unit-group="unitGroup"
                :skip-default-load="true" />
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="!hasData && !isLoading && viewMode !== 'map' && viewMode !== 'split'">
          <i class="bi bi-graph-up icon-3x mb-3"></i>
          <h5>No Data Yet</h5>
          <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="isLoading">
          <i class="bi bi-arrow-repeat spin-icon icon-3x mb-3"></i>
          <h5>Loading Model Data...</h5>
          <p>Fetching from {{ selectedModels.length }} models</p>
          <div class="progress">
            <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: loadingProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Stats Panel (Right) -->
      <div class="stats-panel" :class="{ collapsed: statsPanelCollapsed }">
        <div class="panel-header" @click="statsPanelCollapsed = !statsPanelCollapsed">
          <span>Statistics</span>
          <i class="bi" :class="statsPanelCollapsed ? 'bi-chevron-left' : 'bi-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!statsPanelCollapsed && hasData">
          <!-- Stats Table -->
          <table class="stats-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Min</th>
                <th>Max</th>
                <th>Avg</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="stat in modelStats" 
                :key="stat.id"
                :class="{ 'is-baseline': stat.id === baselineModel }"
              >
                <td>
                  <span class="stat-color" :style="{ backgroundColor: stat.color }"></span>
                  {{ stat.name }}
                </td>
                <td :class="{ 'is-min': stat.isOverallMin }">{{ formatValue(stat.min) }}</td>
                <td :class="{ 'is-max': stat.isOverallMax }">{{ formatValue(stat.max) }}</td>
                <td>{{ formatValue(stat.avg) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Spread Analysis -->
          <div class="spread-analysis" v-if="spreadStats">
            <h6>Model Spread</h6>
            <div class="spread-item">
              <span>Range:</span>
              <strong>{{ formatValue(spreadStats.range) }}</strong>
            </div>
            <div class="spread-item">
              <span>Std Dev:</span>
              <strong>{{ formatValue(spreadStats.stdDev) }}</strong>
            </div>
            <div class="spread-item">
              <span>Agreement:</span>
              <strong :class="spreadStats.agreementClass">{{ spreadStats.agreement }}</strong>
            </div>
          </div>

          <!-- Attribution Info -->
          <div class="attribution-info" v-if="hasAttributionData">
            <h6>Model Attribution</h6>
            <div class="attribution-list">
              <div v-for="(attrs, modelId) in attributionData" :key="modelId" class="attribution-item">
                <div class="attribution-model">{{ getModelName(modelId) }}</div>
                <div v-for="attr in attrs" :key="attr.modelId" class="attribution-detail">
                  <span class="attr-model">{{ attr.modelId }}</span>
                  <span class="attr-run">{{ attr.runId }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Model Rankings -->
          <div class="model-rankings" v-if="baselineModel && modelRankings.length">
            <h6>Deviation from {{ getModelName(baselineModel) }}{{ hasV2Version(baselineModel) ? ' ⚠️' : '' }}</h6>
            <div 
              v-for="rank in modelRankings" 
              :key="rank.id" 
              class="ranking-item"
            >
              <span class="rank-color" :style="{ backgroundColor: rank.color }"></span>
              <span class="rank-name">{{ rank.name }}</span>
              <span class="rank-legacy" v-if="hasV2Version(rank.id)">⚠️</span>
              <span class="rank-v2" v-if="isV2Model(rank.id)">✓</span>
              <span class="rank-deviation" :class="rank.deviationClass">
                {{ rank.deviationText }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Modal -->
    <div class="modal fade" :class="{ show: showHelpModal }" v-if="showHelpModal" @click.self="showHelpModal = false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Model Comparison Help</h5>
            <button type="button" class="btn-close" @click="showHelpModal = false"></button>
          </div>
          <div class="modal-body">
            <h6>Available Models</h6>
            <p>Compare forecasts from multiple weather models including:</p>
            <ul>
              <li><strong>Blended Results</strong> - Our optimized combined forecast</li>
              <li><strong>US Regional</strong> - HRRR, NAM, NDFD (higher resolution for US)</li>
              <li><strong>European</strong> - ECMWF, ICON-EU, ICON-D2, AROME, ARPEGE</li>
              <li><strong>Global</strong> - GFS, ICON Global, GDPS, UK Met Office</li>
              <li><strong>AI/ML</strong> - AI-powered models like ECMWF AIFS and AI GFS</li>
              <li><strong>Ensemble Models</strong> - GFS Ensemble (gfes_2) with 31 members showing forecast uncertainty</li>
            </ul>
            
            <h6>Ensemble Spread Visualization</h6>
            <p>When selecting ensemble models like <strong>GFS Ensemble (Spread)</strong>:</p>
            <ul>
              <li>A shaded area shows the range between minimum and maximum ensemble member values</li>
              <li>The center line shows the ensemble mean</li>
              <li>Toggle "Show Spread Range" in the Ensemble Models group to enable/disable</li>
              <li>Adjust opacity slider to change the spread area visibility</li>
              <li>Hover tooltips show both the mean and the full spread range</li>
            </ul>
            
            <h6>Comparison Views</h6>
            <ul>
              <li><strong>Chart</strong> - Line chart showing all selected models</li>
              <li><strong>Table</strong> - Tabular data with all values</li>
              <li><strong>Difference</strong> - Shows deviation from selected baseline</li>
            </ul>
            
            <h6>Tips</h6>
            <ul>
              <li>Hover over the chart to see values at specific times</li>
              <li>Click legend items to highlight individual models</li>
              <li>Use presets to quickly select model groups</li>
              <li>Select a baseline model to see relative differences</li>
              <li>Use the "Ensemble" preset to quickly compare ensemble forecasts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { loadModelsFromApi } from '../data/weatherMapLayers.js';
import {VcGlobal} from "/src/visualcrossing-core/vcglobal_conn";
import WeatherMapAi from './WeatherMapAi.vue';
// Legacy models - these use the old forecastDataset parameter
// Note: Legacy models use prefixed IDs (legacy_xxx) to avoid conflicts with V2 models
// The apiModelId property stores the actual value to send to the API
const LEGACY_MODELS = [
  { id: 'default', name: 'Default', color: '#000000', group: 'blended', width: 3, noForecastDataset: true, isLegacy: false },
  { id: 'default_v2', name: 'Default V2', color: '#2E7D32', group: 'blended', width: 3, noForecastDataset: true, useV2Forecast: true, isLegacy: false },
  { id: 'legacy_result', apiModelId: 'result', name: 'Result (Legacy)', color: '#000000', group: 'legacy', width: 4, isLegacy: true },
  { id: 'legacy_hrrr', apiModelId: 'hrrr', name: 'HRRR (Legacy)', color: '#0000FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_nam', apiModelId: 'nam', name: 'NAM (Legacy)', color: '#FFFF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_ndfd', apiModelId: 'ndfd', name: 'NDFD (Legacy)', color: '#FF00FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfs', apiModelId: 'gfs', name: 'GFS (Legacy)', color: '#FF0000', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfes', apiModelId: 'gfes', name: 'GFS Ensemble (Legacy)', color: '#CFCFCF', group: 'legacy', isLegacy: true },
  { id: 'legacy_usblended', apiModelId: 'usblended', name: 'US Blended (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_vcusblended', apiModelId: 'vcusblended', name: 'VC US Blended (Legacy)', color: '#00FFFF', group: 'legacy', width: 2, isLegacy: true },
  { id: 'legacy_icond2', apiModelId: 'icond2', name: 'ICON-D2 (Legacy)', color: '#AAFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconeu', apiModelId: 'iconeu', name: 'ICON-EU (Legacy)', color: '#00FFFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_ecmwf', apiModelId: 'ecmwf', name: 'ECMWF (Legacy)', color: '#00FF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconglobal', apiModelId: 'iconglobal', name: 'ICON Global (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gdps', apiModelId: 'gdps', name: 'GDPS (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_ukmo_global', apiModelId: 'ukmo_global', name: 'UK Met Global (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
];

// V2 model color palette (assigned dynamically when loaded from API)
const V2_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4',
  '#E91E63', '#3F51B5', '#009688', '#FFC107', '#795548',
  '#607D8B', '#8BC34A', '#CDDC39', '#FF5722', '#673AB7',
  '#03A9F4', '#4DB6AC', '#FFB74D', '#BA68C8', '#81D4FA'
];

// Weather elements with display names
const ELEMENTS = [
  { id: 'datetime', name: 'Date/Time', hidden: true },
  { id: 'datetimeEpoch', name: 'Epoch', hidden: true },
  { id: 'icon', name: 'Icon', hidden: true },
  { id: 'temp', name: 'Temperature' },
  { id: 'feelslike', name: 'Feels Like' },
  { id: 'humidity', name: 'Humidity' },
  { id: 'dew', name: 'Dew Point' },
  { id: 'precip', name: 'Precipitation' },
  { id: 'precipprob', name: 'Precip Probability' },
  { id: 'snow', name: 'Snow' },
  { id: 'snowdepth', name: 'Snow Depth' },
  { id: 'windspeed', name: 'Wind Speed' },
  { id: 'windgust', name: 'Wind Gust' },
  { id: 'winddir', name: 'Wind Direction' },
  { id: 'pressure', name: 'Pressure' },
  { id: 'visibility', name: 'Visibility' },
  { id: 'cloudcover', name: 'Cloud Cover' },
  { id: 'uvindex', name: 'UV Index' },
  { id: 'solarradiation', name: 'Solar Radiation' },
  { id: 'solarenergy', name: 'Solar Energy' },
];

export default {
  name: 'ModelComparison',
  
  components: {
    WeatherMapAi
  },
  
  props: {
    apiKey: {
      type: String,
      default: ''
    },
    initialLocation: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // Session/Auth State (populated from window.vcCommunicator)
      sessionId: null,
      sessionState: null,
      apiKeyOverride: '', // User-provided API key override
      serverRootOverride: 'https://weather6.visualcrossing.com', // Override VcGlobal.SERVER_ROOT for this component (e.g., 'https://weather.visualcrossing.com')
      
      // Location - default to New York
      location: { lat: 40.7128, lon: -74.006, name: 'New York, NY' },
      locationText: 'New York, NY',
      isGeocoding: false,
      
      // Date range
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      
      // Options
      timeResolution: 'hours',
      selectedElement: 'temp',
      baselineModel: 'default',
      viewMode: 'split',
      unitGroup: 'metric',  // metric, us, uk, base
      
      // Model selection
      selectedModels: ['default'],
      expandedGroups: {
        combinations: true,
        v2: true,
        legacy: false
      },
      
      // V2 Models loaded from API
      v2Models: [],
      v2ModelsLoading: false,
      v2ModelsLoaded: false,
      
      // Ensemble spread options
      showEnsembleSpread: true,
      ensembleSpreadOpacity: 0.3,
      modelPanelCollapsed: false,
      statsPanelCollapsed: false,
      
      // API Options
      optionNointerpolate: true,
      optionAttribution: true,
      optionUsefcst: true,
      
      // Data
      queryData: null,
      attributionData: {},  // Store attribution from each model query
      isLoading: false,
      loadingProgress: 0,
      
      // UI state
      showHelpModal: false,
      highlightedModel: null,
      
      // Map integration
      mapSelectedModel: '',  // Selected model to show on map
      mapInitialized: false,
      mapSplitInitialized: false,
      
      // Model runs feature
      showModelRuns: false,  // Checkbox to enable model runs mode
      modelRunsLoading: false,
      modelRunsData: {},  // { modelId: { runs: [...], model: '...' } }
      selectedModelRuns: [],  // Array of { modelId, runId, displayName } objects
      expandedModelRuns: {},  // Track which models have expanded runs list
      
      // Split view resize
      splitChartHeight: 300,  // Initial chart height in pixels
      isResizingSplit: false,
      splitResizeStartY: 0,
      splitResizeStartHeight: 0,
      
      // Track last fetch parameters to detect when refresh is needed
      lastFetchParams: null,
    };
  },

  computed: {
    // Combine legacy models and V2 models loaded from API
    allModels() {
      return [...LEGACY_MODELS, ...this.v2Models];
    },
    
    modelGroups() {
      const groups = {
        v2: [],
        legacy: []
      };
      
      // Add V2 models and sort alphabetically by name
      this.v2Models.forEach(model => {
        groups.v2.push(model);
      });
      groups.v2.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      
      // Add legacy models and sort alphabetically by name
      LEGACY_MODELS.forEach(model => {
        if (model.isLegacy) {
          groups.legacy.push(model);
        }
      });
      groups.legacy.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      
      return groups;
    },
    
    modelsWithRuns() {
      // Return V2 models that have available runs data, sorted alphabetically
      return this.v2Models
        .filter(model => this.hasAvailableRuns(model.id))
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    },
    
    hasEnsembleModel() {
      return this.selectedModels.some(id => {
        const model = this.allModels.find(m => m.id === id);
        return model && model.isEnsemble;
      });
    },
    
    // Server root URL - can be overridden for testing/development
    serverRoot() {
      if (this.serverRootOverride) {
        return this.serverRootOverride.replace(/\/$/, ''); // Remove trailing slash if present
      }
      return VcGlobal.SERVER_ROOT;
    },
    
    // Location key for forcing re-render of model bounds checks
    locationKey() {
      if (!this.location) return 'none';
      return `${this.location.lat.toFixed(2)}_${this.location.lon.toFixed(2)}`;
    },
    
    selectedModelCount() {
      return this.selectedModels.length;
    },
    
    totalModelCount() {
      return this.allModels.length;
    },
    
    selectedModelConfigs() {
      const configs = this.selectedModels.map(id => 
        this.allModels.find(m => m.id === id)
      ).filter(Boolean);
      
      // Add run configs from queryData if available
      if (this.showModelRuns && this.queryData) {
        Object.keys(this.queryData).forEach(key => {
          const data = this.queryData[key];
          if (data && data._runDisplayConfig) {
            configs.push(data._runDisplayConfig);
          }
        });
      }
      
      return configs;
    },
    
    availableElements() {
      return ELEMENTS.filter(el => !el.hidden);
    },
    
    /**
     * Get the effective API key to use for API requests
     * Priority: 1) Prop apiKey, 2) User override, 3) Session key, 4) window.vcApiKey
     */
    weatherApiKey() {
      // First check prop
      if (this.apiKey && this.apiKey.trim()) {
        return this.apiKey.trim();
      }
      // Then user override
      if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
        return this.apiKeyOverride.trim();
      }
      // Then session state
      if (this.sessionState?.apiKey) {
        return this.sessionState.apiKey;
      }
      // Finally window global
      return window.vcApiKey || '';
    },
    
    hasValidApiKey() {
      return !!this.weatherApiKey;
    },
    
    hasSession() {
      return !!this.sessionState?.apiKey;
    },
    
    canFetch() {
      const hasModelsOrRuns = this.selectedModels.length > 0 || (this.showModelRuns && this.selectedModelRuns.length > 0);
      return this.location && hasModelsOrRuns && this.startDate && this.endDate && this.hasValidApiKey;
    },
    
    // Current fetch parameters - used to detect if settings have changed
    currentFetchParams() {
      return {
        location: this.location ? `${this.location.lat},${this.location.lon}` : null,
        startDate: this.startDate,
        endDate: this.endDate,
        showModelRuns: this.showModelRuns,
        selectedModels: this.showModelRuns ? [] : [...this.selectedModels].sort(),
        selectedModelRuns: this.showModelRuns ? this.selectedModelRuns.map(r => `${r.modelId}:${r.runId}`).sort() : [],
        timeResolution: this.timeResolution,
        unitGroup: this.unitGroup,
        optionUsefcst: this.optionUsefcst,
        optionNointerpolate: this.optionNointerpolate,
        optionAttribution: this.optionAttribution,
      };
    },
    
    // Check if current settings differ from last fetch
    needsRefresh() {
      if (!this.hasData || !this.lastFetchParams) return false;
      const current = this.currentFetchParams;
      const last = this.lastFetchParams;
      return JSON.stringify(current) !== JSON.stringify(last);
    },
    
    hasData() {
      return this.queryData && Object.keys(this.queryData).length > 0;
    },
    
    hasAttributionData() {
      return this.attributionData && Object.keys(this.attributionData).length > 0;
    },
    
    // Map element display name
    mapElementName() {
      const el = ELEMENTS.find(e => e.id === this.selectedElement);
      return el ? el.name : this.selectedElement;
    },
    
    // Map element ID - converts comparison elements to map-compatible elements
    // The map component uses different element names (e.g., 'precipcomposite' instead of 'precip')
    mapElement() {
      const elementMapping = {
        'temp': 'temp',
        'feelslike': 'temp',  // Map doesn't have feelslike, use temp
        'humidity': 'humidity',
        'dew': 'temp',  // Map doesn't have dew, use temp
        'precip': 'precipcomposite',  // Map uses precipcomposite for precipitation
        'precipprob': 'precipcomposite',
        'snow': 'precipcomposite',
        'snowdepth': 'precipcomposite',
        'windspeed': 'windspeed',
        'windgust': 'windspeed',
        'winddir': 'wind',
        'pressure': 'sealevelpressure',
        'visibility': 'temp',  // Map doesn't have visibility, use temp
        'cloudcover': 'cloudcover',
        'uvindex': 'temp',  // Map doesn't have uvindex, use temp
        'solarradiation': 'solarradiation'
      };
      return elementMapping[this.selectedElement] || 'temp';
    },
    
    // Map selected model display name
    mapSelectedModelName() {
      if (!this.mapSelectedModel) return '';
      const model = this.allModels.find(m => m.id === this.mapSelectedModel);
      return model ? model.name : this.mapSelectedModel;
    },
    
    // Get the first selected V2 model for map display
    defaultMapModel() {
      // Prefer V2 models over legacy
      const v2Selected = this.selectedModels.find(id => this.isV2Model(id));
      if (v2Selected) return v2Selected;
      // Fall back to first selected
      return this.selectedModels[0] || 'hybrid';
    },
    
    // Map model IDs that should be shown in WeatherMapAi
    // Converts selectedModels to their API model IDs for the map component
    // Only includes V2 models (not legacy) since only V2 models have map tiles
    mapAllowedModels() {
      // Complete mapping from V2 API model names to WeatherMapAi model IDs
      // WeatherMapAi model IDs are from weatherMapLayers.js STATIC_MODELS_CONFIG
      const v2ToMapMapping = {
        // Hybrid/Default
        'default': 'hybrid',
        'hybrid': 'hybrid',
        // US Regional
        'hrrr': 'hrrr',
        'hrrr_2': 'hrrr',
        'hrrr_subhourly': 'hrrrsubhourly',
        'hrrr_subhourly_2': 'hrrrsubhourly',
        'nam': 'nam',
        'nam_2': 'nam',
        'ndfd': 'ndfd',
        'ndfd_2': 'ndfd',
        // Global
        'gfs': 'gfs',
        'gfs_2': 'gfs',
        'gfes': 'gfes',
        'ecmwf': 'ecmwf',
        'ecmwf_2': 'ecmwf',
        'ecmwf_ifs': 'ecmwf',
        'ecmwf_aifs': 'ecmwfaifs',
        'ecmwfaifs': 'ecmwfaifs',
        'aigfs': 'aigfs',
        'ai_gfs': 'aigfs',
        // ICON
        'icon_global': 'iconglobal',
        'icon-global': 'iconglobal',
        'iconglobal': 'iconglobal',
        'icon_global_2': 'iconglobal',
        'icon_d2': 'icond2',
        'icon-d2': 'icond2',
        'icond2': 'icond2',
        'icon_eu': 'iconeu',
        'iconeu': 'iconeu',
        // UK Met Office
        'ukmo_global': 'ukmo',
        'ukmo_global_2': 'ukmo',
        'ukmo-global': 'ukmo',
        'ukmetoffice': 'ukmo',
        'ukmo_ukv': 'ukmo_ukv',
        'ukmo_ukv_2': 'ukmo_ukv',
        // Canadian
        'gdps': 'gdps',
        'gdps_2': 'gdps',
        'hrdps': 'hrdps',
        'hrdps_2': 'hrdps',
        'rdps': 'rdps',
        'rdps_2': 'rdps',
        // French
        'arome': 'arome',
        'arome_2': 'arome',
        'arpege': 'arpege',
        'arpege_2': 'arpege',
        // Radar
        'radar': 'radar'
      };
      
      const mapModels = [];
      this.selectedModels.forEach(modelId => {
        // Skip legacy models - they don't have map tiles
        const model = this.allModels.find(m => m.id === modelId);
        if (model && model.isLegacy) {
          return; // Skip legacy models
        }
        
        // Check for direct mapping by model ID
        if (v2ToMapMapping[modelId]) {
          mapModels.push(v2ToMapMapping[modelId]);
          return;
        }
        
        // For V2 models, get the API model ID and check mapping
        if (model) {
          const apiModelId = (model.apiModelId || model.model || model.id).toLowerCase();
          if (v2ToMapMapping[apiModelId]) {
            mapModels.push(v2ToMapMapping[apiModelId]);
          }
        }
      });
      
      // Always include hybrid if we have models
      if (mapModels.length > 0 && !mapModels.includes('hybrid')) {
        mapModels.unshift('hybrid');
      }
      
      // Return unique values
      return [...new Set(mapModels)];
    },
    
    // Initial model to select in the map - first model from selectedModels that has a map equivalent
    mapInitialModel() {
      // Use first from mapAllowedModels (which already mapped the selectedModels)
      // Skip hybrid and use first actual model if available
      const nonHybrid = this.mapAllowedModels.find(m => m !== 'hybrid');
      return nonHybrid || this.mapAllowedModels[0] || 'hybrid';
    },

    tableData() {
      if (!this.hasData) return [];
      
      const data = [];
      const element = this.selectedElement;
      
      // Collect all unique datetimes from all model configs (including runs)
      const datetimes = new Set();
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelData = this.queryData[config.id];
        if (modelData && modelData.days) {
          modelData.days.forEach(day => {
            if (this.timeResolution === 'hours' && day.hours) {
              day.hours.forEach(hour => datetimes.add(hour.datetimeEpoch));
            } else {
              datetimes.add(day.datetimeEpoch);
            }
          });
        }
      });
      
      // Sort datetimes
      const sortedDatetimes = Array.from(datetimes).sort((a, b) => a - b);
      
      // Build rows
      sortedDatetimes.forEach(dt => {
        const row = { datetime: dt };
        let hasAnyValue = false;
        
        this.selectedModelConfigs.forEach(config => {
          if (!config) return;
          const modelData = this.queryData[config.id];
          if (modelData && modelData.days) {
            const value = this.getValueAtTime(modelData, dt, element);
            row[config.id] = value;
            if (value !== null && value !== undefined) {
              hasAnyValue = true;
            }
          }
        });
        
        // Only include rows that have at least one non-null value
        if (hasAnyValue) {
          data.push(row);
        }
      });
      
      return data;
    },
    
    modelStats() {
      if (!this.hasData) return [];
      
      const element = this.selectedElement;
      const stats = [];
      
      let overallMin = Infinity;
      let overallMax = -Infinity;
      let minModelId = null;
      let maxModelId = null;
      
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelId = config.id;
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const values = this.extractValues(modelData, element);
        if (values.length === 0) return;
        
        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        
        if (min < overallMin) {
          overallMin = min;
          minModelId = modelId;
        }
        if (max > overallMax) {
          overallMax = max;
          maxModelId = modelId;
        }
        
        stats.push({
          id: modelId,
          name: config.name || modelId,
          color: config.color || '#888',
          min,
          max,
          avg
        });
      });
      
      // Mark overall min/max
      stats.forEach(s => {
        s.isOverallMin = s.id === minModelId;
        s.isOverallMax = s.id === maxModelId;
      });
      
      return stats;
    },
    
    spreadStats() {
      if (this.modelStats.length < 2) return null;
      
      const avgs = this.modelStats.map(s => s.avg);
      const min = Math.min(...avgs);
      const max = Math.max(...avgs);
      const range = max - min;
      
      const mean = avgs.reduce((a, b) => a + b, 0) / avgs.length;
      const variance = avgs.reduce((a, v) => a + Math.pow(v - mean, 2), 0) / avgs.length;
      const stdDev = Math.sqrt(variance);
      
      let agreement = 'Low';
      let agreementClass = 'text-danger';
      
      if (stdDev < 2) {
        agreement = 'High';
        agreementClass = 'text-success';
      } else if (stdDev < 5) {
        agreement = 'Moderate';
        agreementClass = 'text-warning';
      }
      
      return { range, stdDev, agreement, agreementClass };
    },
    
    modelRankings() {
      if (!this.baselineModel || !this.hasData) return [];
      
      const baselineStat = this.modelStats.find(s => s.id === this.baselineModel);
      if (!baselineStat) return [];
      
      return this.modelStats
        .filter(s => s.id !== this.baselineModel)
        .map(s => {
          const deviation = s.avg - baselineStat.avg;
          const model = this.allModels.find(m => m.id === s.id);
          
          return {
            id: s.id,
            name: model?.name || s.id,
            color: model?.color || '#888',
            deviation,
            deviationText: deviation >= 0 ? `+${this.formatValue(deviation)}` : this.formatValue(deviation),
            deviationClass: deviation >= 0 ? 'text-danger' : 'text-primary'
          };
        })
        .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation));
    }
  },

  methods: {
    // Map integration methods
    initializeMapView() {
      // Set default map model if not set
      if (!this.mapSelectedModel) {
        this.mapSelectedModel = this.defaultMapModel;
      }
      
      // Use nextTick to ensure component is mounted
      this.$nextTick(() => {
        const mapRef = this.viewMode === 'split' ? this.$refs.weatherMapSplit : this.$refs.weatherMap;
        if (mapRef && mapRef.map) {
          // Sync location
          this.syncMapLocation();
          // Sync element/model
          this.syncMapElement();
          this.syncMapModel();
        }
      });
    },
    
    syncMapLocation() {
      if (!this.location) return;
      
      const syncToMap = (mapRef) => {
        if (mapRef && mapRef.map) {
          mapRef.map.setView([this.location.lat, this.location.lon], 8);
        }
      };
      
      if (this.viewMode === 'map' && this.$refs.weatherMap) {
        syncToMap(this.$refs.weatherMap);
      }
      if (this.viewMode === 'split' && this.$refs.weatherMapSplit) {
        syncToMap(this.$refs.weatherMapSplit);
      }
    },
    
    syncMapElement() {
      // Use the mapElement computed property which properly maps to map-compatible elements
      const mapElement = this.mapElement;
      
      const syncToMap = (mapRef) => {
        if (mapRef) {
          mapRef.quickSelectElement = mapElement;
        }
      };
      
      if (this.viewMode === 'map' && this.$refs.weatherMap) {
        syncToMap(this.$refs.weatherMap);
      }
      if (this.viewMode === 'split' && this.$refs.weatherMapSplit) {
        syncToMap(this.$refs.weatherMapSplit);
      }
    },
    
    syncMapModel() {
      // Only sync V2 models as legacy models don't have map tiles
      const model = this.allModels.find(m => m.id === this.mapSelectedModel);
      if (!model) return;
      
      // Use the apiModelId or id for map model
      const mapModel = model.apiModelId || model.id;
      
      const syncToMap = (mapRef) => {
        if (mapRef && mapRef.quickSelectModel !== undefined) {
          // Only set if it's a valid map model
          if (['hybrid', 'hrrr', 'nam', 'gfs', 'iconglobal', 'ecmwf', 'ukmo_global', 'radar'].includes(mapModel)) {
            mapRef.quickSelectModel = mapModel;
          }
        }
      };
      
      if (this.viewMode === 'map' && this.$refs.weatherMap) {
        syncToMap(this.$refs.weatherMap);
      }
      if (this.viewMode === 'split' && this.$refs.weatherMapSplit) {
        syncToMap(this.$refs.weatherMapSplit);
      }
    },

    // Date helpers
    getDefaultStartDate() {
      const d = new Date();
      return d.toISOString().split('T')[0];
    },
    
    getDefaultEndDate() {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    },
    
    setDateRange(preset) {
      const today = new Date();
      this.startDate = today.toISOString().split('T')[0];
      
      const end = new Date(today);
      switch (preset) {
        case 'today':
          break;
        case '3days':
          end.setDate(end.getDate() + 3);
          break;
        case 'week':
          end.setDate(end.getDate() + 7);
          break;
        case '2weeks':
          end.setDate(end.getDate() + 14);
          break;
      }
      this.endDate = end.toISOString().split('T')[0];
    },
    
    // Location
    async geocodeLocation() {
      if (!this.locationText.trim()) return;
      
      this.isGeocoding = true;
      try {
        // Use VC geocoding API or browser geolocation
        const vcCommunicator = window.vcCommunicator;
        if (vcCommunicator && vcCommunicator.geocode) {
          const result = await vcCommunicator.geocode(this.locationText);
          if (result && result.length > 0) {
            this.location = {
              lat: result[0].latitude,
              lon: result[0].longitude,
              name: result[0].resolvedAddress || this.locationText
            };
          }
        } else {
          // Fallback: treat as coordinates if it looks like lat,lon
          const match = this.locationText.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
          if (match) {
            this.location = {
              lat: parseFloat(match[1]),
              lon: parseFloat(match[2]),
              name: this.locationText
            };
          } else {
            this.location = {
              lat: 40.7128,
              lon: -74.006,
              name: this.locationText
            };
          }
        }
      } catch (err) {
        console.error('Geocoding error:', err);
      } finally {
        this.isGeocoding = false;
      }
    },
    
    useCurrentLocation() {
      if (!navigator.geolocation) return;
      
      this.isGeocoding = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.location = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            name: 'Current Location'
          };
          this.locationText = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
          this.isGeocoding = false;
        },
        (err) => {
          console.error('Geolocation error:', err);
          this.isGeocoding = false;
        }
      );
    },
    
    // Model selection
    toggleGroup(group) {
      this.expandedGroups[group] = !this.expandedGroups[group];
    },
    
    selectPreset(preset) {
      switch (preset) {
        case 'all':
          this.selectedModels = this.allModels.map(m => m.id);
          break;
        case 'none':
          this.selectedModels = [];
          break;
        case 'v2':
          // Select only V2 models (loaded from API)
          this.selectedModels = this.v2Models.map(m => m.id);
          this.expandedGroups.v2 = true;
          break;
        case 'legacy':
          // Select only legacy models
          this.selectedModels = LEGACY_MODELS.filter(m => m.isLegacy).map(m => m.id);
          this.expandedGroups.legacy = true;
          break;
      }
    },
    
    getModelName(id) {
      const model = this.allModels.find(m => m.id === id);
      return model?.name || id;
    },
    
    // Get model display name with legacy indicator
    getModelDisplayName(id) {
      const model = this.allModels.find(m => m.id === id);
      const name = model?.name || id;
      if (model && model.isLegacy) {
        return name + ' ⚠️';  // Legacy indicator
      }
      return name;
    },
    
    // Check if a model is a V2 model (loaded from API, not legacy)
    isV2Model(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && !model.isLegacy && model.id !== 'default';
    },
    
    // Check if this is a legacy model
    isLegacyModel(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && model.isLegacy;
    },
    
    // Check if a model's bounds contain the current location
    isModelInBounds(model) {
      if (!model || !model.bounds || !this.location) return true;  // No bounds = global
      const { lat, lon } = this.location;
      const { minLat, maxLat, minLon, maxLon } = model.bounds;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    
    // Legacy compatibility - no longer needed but kept for template references
    hasV2Version(modelId) {
      return false;  // Simplified - legacy/V2 now determined by isLegacy flag
    },
    
    // Get the base model name (without _2 suffix)
    getBaseModelId(modelId) {
      if (modelId.endsWith('_2')) {
        return modelId.slice(0, -2);
      }
      return modelId;
    },
    
    // Load V2 models from API
    async loadV2Models() {
      if (this.v2ModelsLoaded || this.v2ModelsLoading) return;
      
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.log('No API key, skipping V2 models load');
        return;
      }
      
      this.v2ModelsLoading = true;
      
      try {
        const result = await loadModelsFromApi(this.serverRoot, apiKey);
        
        if (result.fromApi && result.modelsConfig) {
          // Convert API models to our format
          // Use the 'model' field from API for forecastDataset (e.g., 'ukmo_global_2')
          // The 'id' field is for display/selection (e.g., 'ukmo')
          this.v2Models = result.modelsConfig.map((modelConfig, index) => ({
            id: modelConfig.id,  // Internal ID for component (e.g., 'ukmo')
            apiModelId: modelConfig.model || modelConfig.id,  // API value for forecastDataset (e.g., 'ukmo_global_2')
            name: result.modelNames[modelConfig.id] || modelConfig.id,
            color: V2_COLORS[index % V2_COLORS.length],
            group: 'v2',
            isLegacy: false,
            isV2: true,
            bounds: modelConfig.bounds  // Include bounds for potential filtering
          }));
          
          console.log(`Loaded ${this.v2Models.length} V2 models from API`);
        }
        
        this.v2ModelsLoaded = true;
      } catch (err) {
        console.error('Failed to load V2 models:', err);
      } finally {
        this.v2ModelsLoading = false;
      }
    },
    
    // Model Runs methods
    async onModelRunsToggle(enable) {
      // Do nothing if already in the requested mode
      if (enable === this.showModelRuns) return;
      
      this.showModelRuns = enable;
      
      // Clear chart data when switching modes
      this.queryData = null;
      this.attributionData = {};
      
      if (this.showModelRuns) {
        // Switching to Model Runs mode - clear selected models
        this.selectedModels = [];
        await this.fetchModelRuns();
      } else {
        // Switching to Models mode - clear runs and set default model
        this.selectedModelRuns = [];
        this.modelRunsData = {};
        this.expandedModelRuns = {};
        this.selectedModels = ['default'];
      }
    },
    
    async fetchModelRuns() {
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.log('No API key, cannot fetch model runs');
        return;
      }
      
      this.modelRunsLoading = true;
      
      try {
        // Calculate date range for last 5 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 5);
        
        const datestart = startDate.toISOString().split('T')[0];
        const dateend = endDate.toISOString().split('T')[0];
        
        // Ensure SERVER_ROOT ends with slash
        const serverRoot = this.serverRoot.endsWith('/') ? this.serverRoot : this.serverRoot + '/';
        const url = `${serverRoot}VisualCrossingWebServices/rest/services/models?key=${apiKey}&datestart=${datestart}&dateend=${dateend}`;
        
        const response = await fetch(url);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        
        const data = await response.json();
        
        // Process and store model runs data
        this.modelRunsData = {};
        if (data.models && Array.isArray(data.models)) {
          data.models.forEach(model => {
            if (model.availableRuns && model.availableRuns.length > 0) {
              this.modelRunsData[model.id] = {
                model: model.model,
                runs: model.availableRuns,
                bounds: model.bounds
              };
            }
          });
        }
        
        console.log(`Loaded runs for ${Object.keys(this.modelRunsData).length} models`);
      } catch (err) {
        console.error('Failed to fetch model runs:', err);
      } finally {
        this.modelRunsLoading = false;
      }
    },
    
    hasAvailableRuns(modelId) {
      // Check by model ID directly or by apiModelId mapping
      const model = this.allModels.find(m => m.id === modelId);
      const checkId = model?.apiModelId || modelId;
      return !!(this.modelRunsData[modelId] || this.modelRunsData[checkId]);
    },
    
    getAvailableRuns(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      const checkId = model?.apiModelId || modelId;
      const data = this.modelRunsData[modelId] || this.modelRunsData[checkId];
      return data?.runs || [];
    },
    
    getAvailableRunsCount(modelId) {
      return this.getAvailableRuns(modelId).length;
    },
    
    toggleModelRuns(modelId) {
      this.expandedModelRuns[modelId] = !this.expandedModelRuns[modelId];
    },
    
    isRunSelected(modelId, runId) {
      return this.selectedModelRuns.some(r => r.modelId === modelId && r.runId === runId);
    },
    
    toggleRunSelection(modelId, run) {
      const idx = this.selectedModelRuns.findIndex(r => r.modelId === modelId && r.runId === run.runId);
      if (idx >= 0) {
        this.selectedModelRuns.splice(idx, 1);
      } else {
        const model = this.allModels.find(m => m.id === modelId);
        // Get the API model ID from the runs data
        const runsData = this.modelRunsData[modelId] || this.modelRunsData[model?.apiModelId || modelId];
        this.selectedModelRuns.push({
          modelId: modelId,
          runId: run.runId,
          runTime: run.runTime,
          displayName: model?.name || modelId,
          apiModelId: runsData?.model || model?.apiModelId || modelId,
          color: model?.color || '#888'
        });
      }
    },
    
    removeSelectedRun(idx) {
      this.selectedModelRuns.splice(idx, 1);
    },
    
    clearSelectedRuns() {
      this.selectedModelRuns = [];
    },
    
    formatRunTime(runTime) {
      if (!runTime) return '';
      const d = new Date(runTime);
      // Display in UTC without timezone conversion
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      }) + ' UTC';
    },
    
    formatRunHour(runTime) {
      if (!runTime) return '';
      const d = new Date(runTime);
      // Just return the hour in UTC
      return d.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
    },
    
    getRunsByDate(modelId) {
      const runs = this.getAvailableRuns(modelId);
      const dateMap = {};
      
      runs.forEach(run => {
        const d = new Date(run.runTime);
        const dateKey = d.toISOString().split('T')[0]; // YYYY-MM-DD
        const dateLabel = d.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC'
        });
        
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            dateLabel: dateLabel,
            runs: []
          };
        }
        dateMap[dateKey].runs.push(run);
      });
      
      // Sort by date descending (most recent first) and runs by time
      return Object.values(dateMap)
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(group => ({
          ...group,
          runs: group.runs.sort((a, b) => new Date(b.runTime) - new Date(a.runTime))
        }));
    },
    
    getRunColor(idx) {
      // Use a color palette for runs
      const runColors = [
        '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1',
        '#D81B60', '#3949AB', '#00897B', '#FFB300', '#6D4C41',
        '#546E7A', '#7CB342', '#C0CA33', '#F4511E', '#5E35B1'
      ];
      return runColors[idx % runColors.length];
    },
    
    // Split view resize methods
    startSplitResize(e) {
      this.isResizingSplit = true;
      this.splitResizeStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      this.splitResizeStartHeight = this.splitChartHeight;
      
      document.addEventListener('mousemove', this.onSplitResize);
      document.addEventListener('mouseup', this.stopSplitResize);
      document.addEventListener('touchmove', this.onSplitResize);
      document.addEventListener('touchend', this.stopSplitResize);
      
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    },
    
    onSplitResize(e) {
      if (!this.isResizingSplit) return;
      
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      const delta = clientY - this.splitResizeStartY;
      const newHeight = this.splitResizeStartHeight + delta;
      
      // Set min/max bounds for chart height
      const container = this.$refs.splitViewContainer;
      const containerHeight = container ? container.clientHeight : 600;
      const minHeight = 150;
      const maxHeight = containerHeight - 200; // Leave room for map
      
      this.splitChartHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
      
      // Trigger chart re-render on resize
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    
    stopSplitResize() {
      this.isResizingSplit = false;
      
      document.removeEventListener('mousemove', this.onSplitResize);
      document.removeEventListener('mouseup', this.stopSplitResize);
      document.removeEventListener('touchmove', this.onSplitResize);
      document.removeEventListener('touchend', this.stopSplitResize);
      
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      
      // Final chart re-render
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    
    // Data fetching
    async fetchData() {
      if (!this.canFetch) return;
      
      this.isLoading = true;
      this.loadingProgress = 0;
      this.queryData = {};
      this.attributionData = {};
      
      const vcCommunicator = window.vcCommunicator;
      const models = [...this.selectedModels];
      
      // Build list of all items to fetch: regular models + selected runs
      const fetchItems = [];
      
      // Track which models have runs selected so we can optionally skip fetching the "latest" run
      const modelsWithSelectedRuns = new Set();
      if (this.showModelRuns && this.selectedModelRuns.length > 0) {
        this.selectedModelRuns.forEach(run => {
          modelsWithSelectedRuns.add(run.modelId);
        });
      }
      
      // Add regular models (only if they're selected AND don't have specific runs selected)
      // If user selected specific runs for a model, skip the "default/latest" fetch for that model
      models.forEach(modelId => {
        // Skip if this model has specific runs selected - we'll only fetch the selected runs
        if (modelsWithSelectedRuns.has(modelId)) {
          return;
        }
        fetchItems.push({
          modelId: modelId,
          runId: null,
          displayKey: modelId  // Key used in queryData
        });
      });
      
      // Add selected model runs (if model runs mode is enabled)
      // These are fetched regardless of whether the parent model checkbox is checked
      if (this.showModelRuns && this.selectedModelRuns.length > 0) {
        this.selectedModelRuns.forEach((run, idx) => {
          fetchItems.push({
            modelId: run.modelId,
            runId: run.runId,
            apiModelId: run.apiModelId,
            displayKey: `${run.modelId}_run_${run.runId}`,  // Unique key for this run
            displayName: `${run.displayName} (${this.formatRunTime(run.runTime)})`,
            color: this.getRunColor(idx)
          });
        });
      }
      
      const totalItems = fetchItems.length;
      
      try {
        for (let i = 0; i < fetchItems.length; i++) {
          const item = fetchItems[i];
          const modelId = item.modelId;
          this.loadingProgress = ((i + 1) / totalItems) * 100;
          
          // Use locationText for API request - the API can resolve location names
          // This allows "paris,france" to work directly without client-side geocoding
          const locationStr = this.locationText.trim() || `${this.location.lat},${this.location.lon}`;
          const include = this.timeResolution === 'hours' ? 'hours' : 'days';
          
          // Build options string from checkboxes
          const optionsList = [];
          if (this.optionUsefcst) optionsList.push('usefcst');
          if (this.optionNointerpolate) optionsList.push('nointerpolate');
          if (this.optionAttribution) optionsList.push('attribution');
          // Add usev2forecast for models with useV2Forecast flag
          const modelConfigForOptions = this.allModels.find(m => m.id === modelId);
          if (modelConfigForOptions?.useV2Forecast) optionsList.push('usev2forecast');
          const optionsStr = optionsList.join(',');
          
          // Build query definition object for vcCommunicator
          const queryDefn = {
            startdate: this.startDate,
            enddate: this.endDate,
            include: include,
            elements: ELEMENTS.map(e => e.id).join(','),
            options: optionsStr,
            unitGroup: this.unitGroup
          };
          
          // Add model/forecastDataset unless model has noForecastDataset flag
          const modelConfig = this.allModels.find(m => m.id === modelId);
          if (modelId && !(modelConfig && modelConfig.noForecastDataset)) {
            // Use apiModelId if available (for legacy models), otherwise use the id directly
            // For runs, use the apiModelId from the run data
            let forecastDataset = item.apiModelId || modelConfig?.apiModelId || modelId;
            
            // Append runId to forecastDataset with colon separator (e.g., iconglobal_2:20260127_12)
            if (item.runId) {
              forecastDataset = `${forecastDataset}:${item.runId}`;
            }
            
            queryDefn.forecastDataset = forecastDataset;
          }
          
          try {
            let data;
            if (vcCommunicator && vcCommunicator.retrieveTimelineData) {
              // vcCommunicator.retrieveTimelineData(defn, location, name, contentType, requireKey, forceGlobalKey)
              data = await vcCommunicator.retrieveTimelineData(queryDefn, locationStr, null, "json", true, false, this.serverRoot);
            } else {
              // Fallback to direct API call
              // Use apiModelId for legacy models, otherwise use the id
              let apiModel = item.apiModelId || modelConfig?.apiModelId || modelId;
              
              // Append runId to model with colon separator (e.g., iconglobal_2:20260127_12)
              if (item.runId) {
                apiModel = `${apiModel}:${item.runId}`;
              }
              
              const fallbackOptions = [optionsStr];
              if (apiModel) fallbackOptions.push(`models:${apiModel}`);
              const options = {
                location: locationStr,
                startdate: this.startDate,
                enddate: this.endDate,
                include: include,
                options: fallbackOptions.join(','),
                elements: ELEMENTS.map(e => e.id).join(',')
              };
              data = await this.fetchFromApi(options);
            }
            
            if (data) {
              // Use displayKey to store data (allows multiple runs of same model)
              const dataKey = item.displayKey;
              this.queryData[dataKey] = data;
              // Store attribution if present
              if (data.attribution) {
                this.attributionData[dataKey] = data.attribution;
              }
              
              // Sync location from first API response (API returns resolved coordinates)
              if (data.latitude !== undefined && data.longitude !== undefined) {
                const apiLat = parseFloat(data.latitude);
                const apiLon = parseFloat(data.longitude);
                // Update location if it differs from what we have
                if (this.location.lat !== apiLat || this.location.lon !== apiLon) {
                  this.location = {
                    lat: apiLat,
                    lon: apiLon,
                    name: data.resolvedAddress || this.location.name
                  };
                  // Update locationText to show resolved coordinates/address
                  if (data.resolvedAddress) {
                    this.locationText = data.resolvedAddress;
                  }
                }
              }
              
              // For runs, add a dynamic entry to track for display purposes
              if (item.runId && item.displayName) {
                // Create a temporary model config for the chart/table display
                data._runDisplayConfig = {
                  id: dataKey,
                  name: item.displayName,
                  color: item.color,
                  isRun: true
                };
              }
            }
          } catch (modelErr) {
            console.warn(`Failed to fetch ${item.displayKey}:`, modelErr);
          }
        }
        
        // Save fetch parameters to detect future changes
        this.lastFetchParams = JSON.parse(JSON.stringify(this.currentFetchParams));
        
        this.$nextTick(() => {
          this.renderChart();
        });
      } catch (err) {
        console.error('Data fetch error:', err);
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchFromApi(options) {
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.error('No API key available');
        return null;
      }
      
      const baseUrl = this.serverRoot + '/VisualCrossingWebServices/rest/services/timeline';
      const unitGroup = options.unitGroup || this.unitGroup || 'metric';
      const url = `${baseUrl}/${encodeURIComponent(options.location)}/${options.startdate}/${options.enddate}?key=${apiKey}&include=${options.include}&options=${options.options}&elements=${options.elements}&unitGroup=${unitGroup}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return response.json();
    },
    
    // Data extraction
    getValueAtTime(modelData, datetimeEpoch, element) {
      if (!modelData.days) return null;
      
      for (const day of modelData.days) {
        if (this.timeResolution === 'hours' && day.hours) {
          const hour = day.hours.find(h => h.datetimeEpoch === datetimeEpoch);
          if (hour && hour[element] !== undefined) return hour[element];
        } else if (day.datetimeEpoch === datetimeEpoch) {
          return day[element];
        }
      }
      return null;
    },
    
    extractValues(modelData, element) {
      const values = [];
      if (!modelData.days) return values;
      
      modelData.days.forEach(day => {
        if (this.timeResolution === 'hours' && day.hours) {
          day.hours.forEach(hour => {
            if (hour[element] !== undefined && hour[element] !== null) {
              values.push(hour[element]);
            }
          });
        } else if (day[element] !== undefined && day[element] !== null) {
          values.push(day[element]);
        }
      });
      
      return values;
    },
    
    // Formatting
    formatValue(val) {
      if (val === null || val === undefined) return '-';
      return typeof val === 'number' ? val.toFixed(1) : val;
    },
    
    formatDateTime(epoch) {
      const d = new Date(epoch * 1000);
      if (this.timeResolution === 'hours') {
        return d.toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    
    getCellClass(row, modelId) {
      if (!this.baselineModel || modelId === this.baselineModel) return '';
      
      const baseVal = row[this.baselineModel];
      const val = row[modelId];
      
      if (baseVal === null || val === null) return '';
      
      const diff = val - baseVal;
      if (Math.abs(diff) < 1) return 'cell-similar';
      return diff > 0 ? 'cell-higher' : 'cell-lower';
    },
    
    highlightModel(modelId) {
      this.highlightedModel = modelId;
      // Could also update chart highlighting here
    },
    
    // Chart rendering
    renderChart() {
      if (!this.hasData) return;
      
      // Render to main chart if in chart/diff mode
      if (this.$refs.chartSvg && this.$refs.chartContainer && (this.viewMode === 'chart' || this.viewMode === 'diff')) {
        this.renderChartToContainer(this.$refs.chartContainer, this.$refs.chartSvg, this.$refs.chartTooltip);
      }
      
      // Also render to split chart if in split mode
      if (this.$refs.splitChartSvg && this.$refs.splitChartContainer && this.viewMode === 'split') {
        this.renderChartToContainer(this.$refs.splitChartContainer, this.$refs.splitChartSvg, this.$refs.splitChartTooltip);
      }
    },
    
    renderChartToContainer(container, svgElement, tooltipElement) {
      const svg = d3.select(svgElement);
      const tooltip = d3.select(tooltipElement);
      
      // Clear previous
      svg.selectAll('*').remove();
      
      // Dimensions
      const margin = { top: 20, right: 30, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 350 - margin.top - margin.bottom;
      
      svg.attr('width', width + margin.left + margin.right)
         .attr('height', height + margin.top + margin.bottom);
      
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Prepare data
      const element = this.selectedElement;
      const allData = [];
      
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelId = config.id;
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const points = [];
        const spreadPoints = []; // For ensemble spread (min/max range)
        const isEnsembleModel = config.isEnsemble;
        
        modelData.days.forEach(day => {
          if (this.timeResolution === 'hours' && day.hours) {
            day.hours.forEach(hour => {
              if (hour[element] !== undefined && hour[element] !== null) {
                const baseValue = this.viewMode === 'diff' && this.baselineModel 
                  ? this.getBaselineValue(hour.datetimeEpoch, element) || 0
                  : 0;
                
                points.push({
                  datetime: hour.datetimeEpoch * 1000,
                  value: hour[element] - baseValue
                });
                
                // Collect ensemble spread data if available
                if (isEnsembleModel && this.showEnsembleSpread) {
                  const minKey = element + 'min';
                  const maxKey = element + 'max';
                  const minVal = hour[minKey] !== undefined ? hour[minKey] : hour[element];
                  const maxVal = hour[maxKey] !== undefined ? hour[maxKey] : hour[element];
                  
                  spreadPoints.push({
                    datetime: hour.datetimeEpoch * 1000,
                    min: minVal - baseValue,
                    max: maxVal - baseValue,
                    value: hour[element] - baseValue
                  });
                }
              }
            });
          } else if (day[element] !== undefined && day[element] !== null) {
            const baseValue = this.viewMode === 'diff' && this.baselineModel
              ? this.getBaselineValue(day.datetimeEpoch, element) || 0
              : 0;
            
            points.push({
              datetime: day.datetimeEpoch * 1000,
              value: day[element] - baseValue
            });
            
            // Collect ensemble spread data if available
            if (isEnsembleModel && this.showEnsembleSpread) {
              const minKey = element + 'min';
              const maxKey = element + 'max';
              const minVal = day[minKey] !== undefined ? day[minKey] : day[element];
              const maxVal = day[maxKey] !== undefined ? day[maxKey] : day[element];
              
              spreadPoints.push({
                datetime: day.datetimeEpoch * 1000,
                min: minVal - baseValue,
                max: maxVal - baseValue,
                value: day[element] - baseValue
              });
            }
          }
        });
        
        if (points.length > 0) {
          allData.push({
            modelId,
            model: config,
            points,
            spreadPoints: isEnsembleModel ? spreadPoints : null,
            isEnsemble: isEnsembleModel
          });
        }
      });
      
      if (allData.length === 0) return;
      
      // Scales - include ensemble spread in extent calculation
      const xExtent = d3.extent(allData.flatMap(d => d.points.map(p => p.datetime)));
      let yValues = allData.flatMap(d => d.points.map(p => p.value));
      
      // Include ensemble spread min/max in y extent
      allData.forEach(d => {
        if (d.spreadPoints && d.spreadPoints.length > 0) {
          d.spreadPoints.forEach(sp => {
            yValues.push(sp.min, sp.max);
          });
        }
      });
      
      const yExtent = d3.extent(yValues);
      const yPadding = (yExtent[1] - yExtent[0]) * 0.1;
      
      const x = d3.scaleTime()
        .domain(xExtent)
        .range([0, width]);
      
      const y = d3.scaleLinear()
        .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
        .range([height, 0]);
      
      // Zero line for diff mode
      if (this.viewMode === 'diff') {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', y(0))
          .attr('y2', y(0))
          .attr('stroke', '#999')
          .attr('stroke-dasharray', '5,5');
      }
      
      // Ensemble spread areas (render first, so lines are on top)
      const area = d3.area()
        .defined(d => d.min !== null && d.max !== null)
        .x(d => x(d.datetime))
        .y0(d => y(d.min))
        .y1(d => y(d.max))
        .curve(d3.curveMonotoneX);
      
      allData.forEach(data => {
        if (data.isEnsemble && data.spreadPoints && data.spreadPoints.length > 0 && this.showEnsembleSpread) {
          g.append('path')
            .datum(data.spreadPoints)
            .attr('class', `ensemble-spread ensemble-${data.modelId}`)
            .attr('fill', data.model?.color || '#888')
            .attr('fill-opacity', this.ensembleSpreadOpacity)
            .attr('stroke', 'none')
            .attr('d', area);
          
          // Add a label for the spread
          const lastPoint = data.spreadPoints[data.spreadPoints.length - 1];
          if (lastPoint) {
            g.append('text')
              .attr('class', 'ensemble-label')
              .attr('x', x(lastPoint.datetime) + 5)
              .attr('y', y((lastPoint.min + lastPoint.max) / 2))
              .attr('fill', data.model?.color || '#888')
              .attr('font-size', '10px')
              .attr('dominant-baseline', 'middle')
              .text(`±${((lastPoint.max - lastPoint.min) / 2).toFixed(1)}`);
          }
        }
      });
      
      // Determine time gap threshold based on resolution
      // Gap is considered "large" if more than 1 interval (1 hour or 1 day)
      const gapThreshold = this.timeResolution === 'hours' 
        ? 60 * 60 * 1000 * 1.5   // 1.5 hours in ms
        : 24 * 60 * 60 * 1000 * 1.5; // 1.5 days in ms
      
      // Helper function to segment points based on time gaps
      const segmentPoints = (points) => {
        if (points.length < 2) return { continuous: [points], gaps: [] };
        
        const continuous = [];
        const gaps = [];
        let currentSegment = [points[0]];
        
        for (let i = 1; i < points.length; i++) {
          const timeDiff = points[i].datetime - points[i - 1].datetime;
          
          if (timeDiff > gapThreshold) {
            // Large gap - end current segment, record gap
            if (currentSegment.length > 0) {
              continuous.push(currentSegment);
            }
            gaps.push([points[i - 1], points[i]]); // Gap connection
            currentSegment = [points[i]];
          } else {
            // Continuous - add to current segment
            currentSegment.push(points[i]);
          }
        }
        
        if (currentSegment.length > 0) {
          continuous.push(currentSegment);
        }
        
        return { continuous, gaps };
      };
      
      // Line generator for continuous segments
      const line = d3.line()
        .defined(d => d.value !== null)
        .x(d => x(d.datetime))
        .y(d => y(d.value))
        .curve(d3.curveMonotoneX);
      
      // Line generator for gap segments (straight lines)
      const gapLine = d3.line()
        .x(d => x(d.datetime))
        .y(d => y(d.value));
      
      allData.forEach(data => {
        const { continuous, gaps } = segmentPoints(data.points);
        const baseColor = data.model?.color || '#888';
        const baseWidth = data.isEnsemble ? 2.5 : (data.model?.width || 1.5);
        
        // Draw continuous line segments (subtle, faded)
        continuous.forEach(segment => {
          if (segment.length >= 2) {
            g.append('path')
              .datum(segment)
              .attr('class', `model-line model-${data.modelId}`)
              .attr('fill', 'none')
              .attr('stroke', baseColor)
              .attr('stroke-width', baseWidth * 0.8)
              .attr('stroke-opacity', 0.4)
              .attr('d', line);
          }
        });
        
        // Draw gap segments (dotted, even more subtle)
        gaps.forEach(gapPair => {
          g.append('path')
            .datum(gapPair)
            .attr('class', `model-line-gap model-${data.modelId}`)
            .attr('fill', 'none')
            .attr('stroke', baseColor)
            .attr('stroke-width', baseWidth * 0.6)
            .attr('stroke-opacity', 0.25)
            .attr('stroke-dasharray', '4,4')
            .attr('d', gapLine);
        });
        
        // Draw data points (prominent circles)
        g.selectAll(`.data-point-${data.modelId}`)
          .data(data.points.filter(p => p.value !== null))
          .enter()
          .append('circle')
          .attr('class', `data-point data-point-${data.modelId}`)
          .attr('cx', d => x(d.datetime))
          .attr('cy', d => y(d.value))
          .attr('r', 4)
          .attr('fill', baseColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.9);
      });
      
      // Axes
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.timeFormat('%m/%d %H:%M')))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');
      
      g.append('g')
        .call(d3.axisLeft(y).ticks(6));
      
      // Tooltip interaction
      const me = this;
      const focus = g.append('line')
        .attr('class', 'focus-line')
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', '#999')
        .attr('stroke-dasharray', '3,3')
        .style('opacity', 0);
      
      svg.on('mousemove', function(event) {
        const [mx] = d3.pointer(event);
        let xVal = x.invert(mx - margin.left);
        
        // Snap to nearest time interval based on resolution
        if (me.timeResolution === 'hours') {
          const hourMs = 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / hourMs) * hourMs);
        } else {
          // Daily - snap to nearest day (midnight)
          const dayMs = 24 * 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / dayMs) * dayMs);
        }
        
        // Update focus line position to snapped time
        const snappedX = x(xVal);
        focus.attr('x1', snappedX)
             .attr('x2', snappedX)
             .style('opacity', 1);
        
        // Build tooltip content - use appropriate format for resolution
        const timeFormat = me.timeResolution === 'hours' ? '%b %d, %H:%M' : '%b %d';
        const utcTimeStr = me.timeResolution === 'hours' 
          ? xVal.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
          : xVal.toISOString().slice(0, 10) + ' UTC';
        let html = `<div class="tooltip-time">${d3.timeFormat(timeFormat)(xVal)}</div>`;
        html += `<div class="tooltip-utc">${utcTimeStr}</div>`;
        
        // Only show values for actual data points - not interpolated
        // Exact match threshold: within 1 minute for hourly, within 1 hour for daily
        const exactMatchThreshold = me.timeResolution === 'hours' ? 60 * 1000 : 60 * 60 * 1000;
        
        allData.forEach(data => {
          if (!data.points || data.points.length === 0) {
            // No data for this model
            html += `<div class="tooltip-row">
              <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
              <span class="tooltip-name">${data.model?.name || data.modelId}</span>
              <span class="tooltip-value">—</span>
            </div>`;
            return;
          }
          
          // Find an exact match at the snapped time
          const exactPoint = data.points.find(p => Math.abs(p.datetime - xVal.getTime()) < exactMatchThreshold);
          
          // Only show value if there's an actual data point at this time
          const hasDataAtTime = !!exactPoint;
          
          // Check for ensemble spread data
          let spreadInfo = '';
          if (hasDataAtTime && data.isEnsemble && data.spreadPoints) {
            const exactSpread = data.spreadPoints.find(p => Math.abs(p.datetime - xVal.getTime()) < exactMatchThreshold);
            if (exactSpread && exactSpread.min !== undefined && exactSpread.max !== undefined) {
              spreadInfo = `<span class="tooltip-spread">(${me.formatValue(exactSpread.min)} - ${me.formatValue(exactSpread.max)})</span>`;
            }
          }
          
          const ensembleIcon = data.isEnsemble ? '<i class=\"bi bi-graph-up\" style=\"font-size:9px;margin-right:2px;\"></i>' : '';
          const displayValue = hasDataAtTime ? `${me.formatValue(exactPoint.value)} ${spreadInfo}` : '—';
          
          html += `<div class="tooltip-row ${data.isEnsemble ? 'is-ensemble' : ''}">
            <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
            <span class="tooltip-name">${ensembleIcon}${data.model?.name || data.modelId}</span>
            <span class="tooltip-value">${displayValue}</span>
          </div>`;
        });
        
        tooltip.select('.tooltip-content').html(html);
        tooltip.style('opacity', 1)
               .style('left', `${mx + 10}px`)
               .style('top', '20px');
      });
      
      // Click to sync map time (only in split mode)
      svg.on('click', function(event) {
        if (me.viewMode !== 'split') return;
        
        const [mx] = d3.pointer(event);
        let xVal = x.invert(mx - margin.left);
        
        // Snap to nearest time interval based on resolution
        if (me.timeResolution === 'hours') {
          const hourMs = 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / hourMs) * hourMs);
        } else {
          const dayMs = 24 * 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / dayMs) * dayMs);
        }
        
        // Sync the map time
        me.syncMapTime(xVal);
      });
      
      svg.on('mouseleave', function() {
        focus.style('opacity', 0);
        tooltip.style('opacity', 0);
      });
    },
    
    // Sync the map to a specific time from chart click
    syncMapTime(dateTime) {
      const mapRef = this.$refs.weatherMapSplit;
      if (mapRef && mapRef.currentTime !== undefined) {
        mapRef.currentTime = dateTime;
        // Refresh map tiles if there's a method for that
        if (typeof mapRef.refreshAllLayers === 'function') {
          mapRef.refreshAllLayers();
        }
      }
    },
    
    getBaselineValue(datetimeEpoch, element) {
      if (!this.baselineModel || !this.queryData[this.baselineModel]) return null;
      return this.getValueAtTime(this.queryData[this.baselineModel], datetimeEpoch, element);
    }
  },

  watch: {
    queryData: {
      handler() {
        this.$nextTick(() => this.renderChart());
      },
      deep: true
    },
    selectedElement() {
      this.$nextTick(() => this.renderChart());
      this.syncMapElement();
    },
    viewMode(newMode) {
      this.$nextTick(() => {
        // Initialize map when entering map or split view
        if (newMode === 'map' || newMode === 'split') {
          this.$nextTick(() => this.initializeMapView());
        }
        // Render chart - use double nextTick for split mode since v-if needs to mount first
        if (newMode === 'split') {
          this.$nextTick(() => this.renderChart());
        } else {
          this.renderChart();
        }
      });
    },
    baselineModel() {
      this.$nextTick(() => this.renderChart());
    },
    showEnsembleSpread() {
      this.$nextTick(() => this.renderChart());
    },
    ensembleSpreadOpacity() {
      this.$nextTick(() => this.renderChart());
    },
    location: {
      handler() {
        this.syncMapLocation();
      },
      deep: true
    },
    selectedModels: {
      handler() {
        // Update map model if current selection is no longer selected
        if (this.mapSelectedModel && !this.selectedModels.includes(this.mapSelectedModel)) {
          this.mapSelectedModel = this.defaultMapModel;
        }
        this.syncMapModel();
      },
      deep: true
    },
    mapSelectedModel() {
      this.syncMapModel();
    },
    initialLocation: {
      handler(val) {
        if (val) {
          this.location = val;
          this.locationText = val.name || `${val.lat}, ${val.lon}`;
        }
      },
      immediate: true
    }
  },

  mounted() {
    window.addEventListener('resize', this.renderChart);
    
    // Initialize session state from vcCommunicator
    const vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;
      
      // Listen for session creation
      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        // Load V2 models when session is available
        this.loadV2Models();
      });
      
      // Listen for session termination
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
      });
      
      // Load V2 models if we already have an API key
      if (this.weatherApiKey) {
        this.loadV2Models();
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.renderChart);
    // Clean up split resize listeners if still active
    document.removeEventListener('mousemove', this.onSplitResize);
    document.removeEventListener('mouseup', this.stopSplitResize);
    document.removeEventListener('touchmove', this.onSplitResize);
    document.removeEventListener('touchend', this.stopSplitResize);
  }
};
</script>

<style scoped>
/* Bootstrap Icons spin animation */
.spin-icon {
  animation: spin-animation 1s linear infinite;
}

@keyframes spin-animation {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Large icon sizing (replaces fa-3x) */
.icon-3x {
  font-size: 3rem;
}

.model-comparison {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
  font-size: 13px;
}

/* Header */
.comparison-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%);
  color: white;
}

.comparison-header h4 {
  font-size: 16px;
  font-weight: 600;
}

.header-actions .btn {
  color: white;
  border-color: rgba(255,255,255,0.3);
}

/* Controls Bar */
.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #dee2e6;
  align-items: flex-start;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.control-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
}

.api-key-status {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.api-key-status .bi-key-fill {
  font-size: 10px;
}

.location-input-wrapper {
  display: flex;
  gap: 4px;
}

.location-input {
  min-width: 200px;
}

.location-display {
  font-size: 11px;
  color: #28a745;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-separator {
  color: #6c757d;
}

.quick-dates {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.quick-date-link {
  font-size: 11px;
  color: #0d6efd;
  text-decoration: none;
  cursor: pointer;
}

.quick-date-link:hover {
  text-decoration: underline;
  color: #0a58ca;
}

/* Out of bounds models */
.model-checkbox.out-of-bounds {
  opacity: 0.6;
}

.model-checkbox.out-of-bounds .model-name {
  text-decoration: line-through;
  color: #999;
}

.out-bounds-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: #dc3545;
  font-size: 12px;
  flex-shrink: 0;
}

/* API status group */
.api-status-group {
  margin-left: auto;
  justify-content: flex-end;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
}

.fetch-btn {
  min-width: 130px;
  transition: background-color 0.2s, border-color 0.2s;
}

.fetch-btn.btn-warning {
  animation: pulse-warning 1.5s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(255, 193, 7, 0); }
}

/* Main Content */
.comparison-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Model Panel */
.model-panel {
  width: 220px;
  background: white;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.model-panel.collapsed {
  width: 40px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  font-weight: 600;
}

.selected-count {
  font-size: 11px;
  color: #6c757d;
  margin-right: auto;
  margin-left: 8px;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.model-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dee2e6;
}

.model-group {
  margin-bottom: 8px;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
}

.group-header:hover {
  background: #dee2e6;
}

.group-models {
  padding: 6px 0 6px 16px;
}

.model-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  cursor: pointer;
  font-size: 12px;
}

.model-checkbox input {
  margin: 0;
}

.model-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.model-badge {
  font-size: 9px;
  padding: 1px 4px;
  background: #17a2b8;
  color: white;
  border-radius: 3px;
  margin-left: auto;
}

.badge-v2 {
  background: #28a745;
  font-weight: 600;
}

.badge-legacy {
  background: #dc3545;
  font-weight: normal;
}

/* V2/Legacy group headers */
.v2-header {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
}

.legacy-header {
  background: rgba(220, 53, 69, 0.1);
  border-left: 3px solid #dc3545;
}

.group-count {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.loading-models {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 12px;
}

.no-models-msg {
  padding: 8px 12px;
  color: #6c757d;
  font-size: 11px;
  font-style: italic;
}

/* Legacy/V2 indicators in various places */
.legend-legacy, .th-legacy, .rank-legacy {
  color: #dc3545;
  font-size: 12px;
  margin-left: 2px;
}

.legend-v2, .th-v2, .rank-v2 {
  color: #28a745;
  font-size: 11px;
  font-weight: 600;
  margin-left: 2px;
}

.legacy-model {
  background: rgba(220, 53, 69, 0.1) !important;
}

.v2-model {
  background: rgba(40, 167, 69, 0.1) !important;
}

/* Results Area */
.results-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
}

.element-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #dee2e6;
  flex-wrap: wrap;
}

.element-selector .form-select {
  width: auto;
  min-width: 150px;
}

.view-toggle {
  margin-left: auto;
}

/* Chart */
.chart-container {
  flex: 1;
  padding: 16px;
  overflow: hidden;
}

.comparison-chart {
  position: relative;
  width: 100%;
  height: 350px;
  background: #fff;
}

.comparison-chart svg {
  width: 100%;
  height: 100%;
}

.chart-tooltip {
  position: absolute;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 100;
  min-width: 150px;
}

.tooltip-content :deep(.tooltip-time) {
  font-weight: 600;
  margin-bottom: 2px;
}

.tooltip-content :deep(.tooltip-utc) {
  font-size: 10px;
  color: #999;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.tooltip-content :deep(.tooltip-row) {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.tooltip-content :deep(.tooltip-color) {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.tooltip-content :deep(.tooltip-value) {
  margin-left: auto;
  font-weight: 600;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid #eee;
}

/* Data point circles */
.data-point {
  transition: r 0.15s ease, opacity 0.15s ease;
  cursor: pointer;
}

.data-point:hover {
  r: 6;
  opacity: 1 !important;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.legend-item.dimmed {
  opacity: 0.3;
}

.legend-color {
  width: 16px;
  height: 3px;
  border-radius: 1px;
}

.legend-spread-indicator {
  font-size: 10px;
  color: #fd7e14;
  margin-left: 2px;
}

.legend-item.is-ensemble .legend-color {
  height: 8px;
  opacity: 0.7;
}

/* Table */
.table-container {
  flex: 1;
  overflow: auto;
  padding: 16px;
}

.table-container .table {
  font-size: 12px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.cell-similar {
  background: rgba(40, 167, 69, 0.1) !important;
}

.cell-higher {
  background: rgba(220, 53, 69, 0.1) !important;
}

.cell-lower {
  background: rgba(0, 123, 255, 0.1) !important;
}

/* Empty/Loading States */
.empty-state,
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 40px;
}

.empty-state i,
.loading-state i {
  color: #adb5bd;
}

.loading-state .progress {
  width: 200px;
  height: 8px;
}

/* Map and Split Empty State Overlays */
.map-empty-overlay,
.split-empty-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
}

.empty-state-content {
  pointer-events: auto;
  text-align: center;
  color: #6c757d;
  padding: 30px;
}

.empty-state-content i {
  color: #adb5bd;
}

.empty-state-content h5 {
  margin-bottom: 8px;
  color: #495057;
}

.empty-state-content p {
  margin: 0;
  font-size: 13px;
  max-width: 280px;
}

/* Stats Panel */
.stats-panel {
  width: 250px;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.stats-panel.collapsed {
  width: 40px;
}

.stats-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.stats-table th,
.stats-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
}

.stats-table th {
  text-align: left;
  font-weight: 600;
  background: #f8f9fa;
}

.stats-table .is-baseline {
  background: rgba(0, 123, 255, 0.1);
}

.stats-table .is-min {
  color: #007bff;
  font-weight: 600;
}

.stats-table .is-max {
  color: #dc3545;
  font-weight: 600;
}

.stat-color {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}

.spread-analysis {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.spread-analysis h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.spread-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
}

.model-rankings {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

/* Options group - vertically centered */
.options-group {
  align-self: center;
}

/* Option checkboxes */
.option-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  background: #f0f0f0;
  transition: background 0.15s;
}

.option-checkbox:hover {
  background: #e0e0e0;
}

.option-checkbox input {
  margin: 0;
  cursor: pointer;
}

.option-checkbox span {
  color: #555;
}

/* Attribution info */
.attribution-info {
  padding: 12px;
  margin: 12px 8px;
  background: #f0f8ff;
  border-radius: 4px;
  border-left: 3px solid #2196F3;
}

.attribution-info h6 {
  font-size: 12px;
  margin-bottom: 8px;
  color: #1565c0;
}

.attribution-list {
  font-size: 11px;
}

.attribution-item {
  margin-bottom: 8px;
}

.attribution-item:last-child {
  margin-bottom: 0;
}

.attribution-model {
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.attribution-detail {
  display: flex;
  gap: 8px;
  padding-left: 8px;
  color: #666;
}

.attr-model {
  font-weight: 500;
}

.attr-run {
  color: #888;
  font-family: monospace;
  font-size: 10px;
}

.model-rankings h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 12px;
}

.rank-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.rank-name {
  flex: 1;
}

.rank-deviation {
  font-weight: 600;
}

/* Modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal.show {
  display: flex;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
}

.modal-content {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-body h6 {
  margin-top: 16px;
  margin-bottom: 8px;
}

.modal-body h6:first-child {
  margin-top: 0;
}

/* Responsive */
@media (max-width: 1200px) {
  .model-panel {
    width: 180px;
  }
  .stats-panel {
    width: 200px;
  }
}

@media (max-width: 992px) {
  .comparison-content {
    flex-direction: column;
  }
  
  .model-panel,
  .stats-panel {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-left: none;
    border-bottom: 1px solid #dee2e6;
  }
  
  .model-panel.collapsed,
  .stats-panel.collapsed {
    max-height: 40px;
    width: 100%;
  }
}

/* Ensemble-specific styles */
.ensemble-badge {
  background: #fd7e14 !important;
  font-size: 8px;
}

.ensemble-options {
  padding: 8px;
  margin-top: 8px;
  background: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffc107;
}

.ensemble-options .form-check {
  margin-bottom: 6px;
}

.ensemble-options .form-check-label {
  font-size: 11px;
}

.opacity-slider {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.opacity-slider input[type="range"] {
  width: 80px;
  height: 4px;
}

/* Ensemble tooltip styles */
.tooltip-content :deep(.tooltip-row.is-ensemble) {
  background: rgba(253, 126, 20, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  margin: 2px -4px;
}

.tooltip-content :deep(.tooltip-spread) {
  font-size: 10px;
  color: #6c757d;
  display: block;
  margin-top: 1px;
}

/* Ensemble legend indicator */
.legend-item.is-ensemble .legend-color {
  height: 10px;
  border-radius: 3px;
  opacity: 0.6;
}

.legend-item.is-ensemble .legend-name::after {
  content: ' (ensemble)';
  font-size: 10px;
  color: #fd7e14;
}

/* Map View Styles */
.map-view-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.map-info-bar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 12px;
  color: #495057;
}

.map-info-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.map-info-text i {
  color: #6c757d;
}

.embedded-map-wrapper {
  flex: 1;
  position: relative;
  min-height: 350px;
}

.embedded-map-wrapper :deep(.weather-map-container) {
  height: 100%;
  width: 100%;
}

/* Split View Styles - Vertical layout (chart on top, map below) */
.split-view-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px;
  background: #dee2e6;
  overflow: hidden;
  position: relative;
}

.split-chart-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 150px;
  overflow: hidden;
}

.split-chart-section .comparison-chart {
  height: 100%;
  min-height: 150px;
}

.split-chart-section .comparison-chart svg {
  width: 100%;
  height: 100%;
}

/* Split view resize handle */
.split-resize-handle {
  flex: 0 0 8px;
  background: #e9ecef;
  cursor: row-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  user-select: none;
  touch-action: none;
  position: relative;
  z-index: 100;
}

.split-resize-handle:hover {
  background: #dee2e6;
}

.split-resize-handle:active {
  background: #ced4da;
}

.resize-handle-bar {
  width: 40px;
  height: 4px;
  background: #adb5bd;
  border-radius: 2px;
  transition: background 0.15s ease;
}

.split-resize-handle:hover .resize-handle-bar {
  background: #6c757d;
}

.split-map-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 350px;
}

.split-map-section .embedded-map-wrapper {
  flex: 1;
  min-height: 350px;
}

/* Mode Toggle Bar */
.mode-toggle-bar {
  display: flex;
  align-items: center;
  padding: 6px 0;
  margin-bottom: 8px;
  gap: 2px;
}

.mode-btn {
  flex: 1;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-btn:first-child {
  border-radius: 4px 0 0 4px;
}

.mode-btn:nth-child(2) {
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.mode-btn:hover {
  background: #f8f9fa;
}

.mode-btn.active {
  background: #1976D2;
  border-color: #1976D2;
  color: white;
}

/* Models Header Bar */
.models-header-bar {
  display: flex;
  justify-content: flex-end;
  padding: 2px 0 4px 0;
}

.clear-link {
  font-size: 10px;
  color: #6c757d;
  text-decoration: none;
}

.clear-link:hover {
  color: #dc3545;
  text-decoration: underline;
}

/* Combinations Group Header */
.combinations-header {
  background: #f8f9fa;
}

/* Selected Runs List */
.selected-runs-list {
  margin-top: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.selected-runs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}

.selected-runs-items {
  max-height: 150px;
  overflow-y: auto;
}

.selected-run-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  margin-bottom: 2px;
  background: white;
  border-radius: 3px;
  font-size: 11px;
  border-left: 3px solid #2196F3;
}

.selected-run-item .run-model-name {
  font-weight: 500;
  color: #333;
}

.selected-run-item .run-time {
  color: #666;
  font-family: monospace;
  font-size: 10px;
  flex: 1;
}

.selected-run-item button {
  padding: 0 4px;
  color: #999;
  background: none;
  border: none;
}

.selected-run-item button:hover {
  color: #dc3545;
}

/* Model Runs Container */
.model-runs-container {
  padding: 8px 0;
}

.runs-loading,
.no-runs-msg {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 11px;
}

.model-runs-group {
  margin-bottom: 12px;
}

.model-runs-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}

.model-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.runs-by-date-container {
  max-height: 110px;
  overflow-y: auto;
  padding-right: 4px;
}

.runs-date-group {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 8px;
}

.runs-date-label {
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  padding-top: 4px;
  min-width: 70px;
  font-weight: 500;
}

.runs-time-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  flex: 1;
}

.run-time-btn {
  padding: 3px 6px;
  font-size: 10px;
  font-family: monospace;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.run-time-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.run-time-btn.selected {
  background: #1976D2;
  border-color: #1976D2;
  color: white;
}

/* Scrollbar styling for runs */
.runs-by-date-container::-webkit-scrollbar {
  width: 4px;
}

.runs-by-date-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.runs-by-date-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.runs-by-date-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
</style>
